import type { AnalyticTrackingConfig } from 'types/analytic-tracking'

declare global {
  interface Window {
    umami?: {
      /**
       * `track(name, data)` sends a custom event. `track(props => ({ ...props, ... }))` merges over
       * the tracker's payload (session token). Object-form `track({ url })` replaces the payload and
       * drops website/referrer — do not use it.
       */
      track: {
        (eventName: string, data?: Record<string, string | number | boolean>): void
        (payload: Record<string, string | number | boolean>): void
        (
          update: (
            props: Record<string, string | number | boolean>,
          ) => Record<string, string | number | boolean | Record<string, string | number | boolean>>,
        ): void
      }
      identify: (uniqueId: string, data?: Record<string, string | number | boolean>) => void
    }
  }
}

export type AnalyticsEventMetadata = Record<string, string | number | boolean | null | undefined>

const ANALYTICS_SCRIPT_PATH = '/script.js'
const ANALYTICS_SCRIPT_ID = 'crm2-analytics'
const ANALYTICS_COLLECT_PATH = '/api/analytic-track'

function stripScriptSuffix(value: string): string {
  return value.replace(/\/+$/, '').replace(/\/script\.js(\?.*)?$/i, '')
}

type AnalyticsRuntime = {
  baseScriptUrl: string
  collectUrl: string
  websiteId: string
  scriptUrl: string
  distinctId?: string
}

let analyticsRuntime: AnalyticsRuntime | null = null
/** Previous pageview path, used as the SPA referrer (document.referrer does not update in-app). */
let lastTrackedUrl = ''

/**
 * Resolve tracker script URL from ANALYTICS_TRACKING_CONFIG.scriptUrl.
 *
 * Supported forms:
 * - `/analytics` — same CRM domain (nginx proxies /analytics/* → analytics container)
 * - `https://crm.example.com/analytics` — explicit same-domain path
 * - `https://analytics.example.com` or `localhost:3011` — separate analytics host
 */
export function resolveAnalyticsScriptUrl(scriptUrl: string): string {
  const trimmed = scriptUrl.trim()
  if (!trimmed) return ''

  // Same-origin path on CRM domain, e.g. /analytics → /analytics/script.js
  if (trimmed.startsWith('/')) {
    const base = trimmed.replace(/\/+$/, '')
    if (/\.js(\?.*)?$/i.test(base)) return base
    return `${base}${ANALYTICS_SCRIPT_PATH}`
  }

  const withProtocol = /^https?:\/\//i.test(trimmed) ? trimmed : `http://${trimmed.replace(/^\/+/, '')}`

  if (/\.js(\?.*)?$/i.test(withProtocol)) return withProtocol
  return `${withProtocol.replace(/\/+$/, '')}${ANALYTICS_SCRIPT_PATH}`
}

function resolveAnalyticsBase(scriptUrl: string): string | null {
  const trimmed = scriptUrl.trim()
  if (!trimmed) return null

  if (trimmed.startsWith('/')) {
    return stripScriptSuffix(trimmed)
  }

  const scriptSrc = resolveAnalyticsScriptUrl(trimmed)
  if (/^https?:\/\//i.test(scriptSrc)) {
    const url = new URL(scriptSrc)
    const basePath = stripScriptSuffix(url.pathname)
    return `${url.origin}${basePath}`
  }

  if (typeof document !== 'undefined') {
    const scriptEl = document.getElementById(ANALYTICS_SCRIPT_ID) as HTMLScriptElement | null
    if (scriptEl?.src) {
      const url = new URL(scriptEl.src)
      const basePath = stripScriptSuffix(url.pathname)
      return `${url.origin}${basePath}`
    }
  }

  return null
}

function resolveCollectUrl(scriptUrl: string): string | null {
  const base = resolveAnalyticsBase(scriptUrl)
  if (!base) return null
  return `${base}${ANALYTICS_COLLECT_PATH}`
}

/**
 * Branch settings may return ANALYTICS_TRACKING_CONFIG as a parsed object or a raw JSON string
 * (when branch_settings type is TEXT instead of JSON).
 */
export function normalizeAnalyticConfig(value: unknown): AnalyticTrackingConfig | undefined {
  if (!value) return undefined

  let parsed: unknown = value
  if (typeof value === 'string') {
    try {
      parsed = JSON.parse(value)
    } catch {
      return undefined
    }
  }

  if (!parsed || typeof parsed !== 'object') return undefined

  const config = parsed as AnalyticTrackingConfig
  if (!config.scriptUrl || !config.websiteId) return undefined

  return {
    ...config,
    enabled: config.enabled === true || (config.enabled as unknown) === 'true',
  }
}

export function configureAnalyticsRuntime(scriptUrl: string, websiteId: string): boolean {
  const baseScriptUrl = resolveAnalyticsBase(scriptUrl)
  if (!baseScriptUrl) return false

  if (analyticsRuntime?.websiteId !== websiteId) {
    lastTrackedUrl = ''
  }

  const distinctId = analyticsRuntime?.websiteId === websiteId ? analyticsRuntime.distinctId : undefined

  analyticsRuntime = {
    baseScriptUrl,
    scriptUrl: resolveAnalyticsScriptUrl(scriptUrl),
    collectUrl: `${baseScriptUrl}${ANALYTICS_COLLECT_PATH}`,
    websiteId,
    distinctId,
  }

  return true
}

export function clearAnalyticsRuntime(): void {
  analyticsRuntime = null
  lastTrackedUrl = ''
}

function sanitizeMetadata(metadata: AnalyticsEventMetadata): Record<string, string | number | boolean> {
  const sanitized: Record<string, string | number | boolean> = {}

  Object.entries(metadata).forEach(([key, value]) => {
    if (value === undefined || value === null) return
    sanitized[key] = value
  })

  return sanitized
}

function getPageUrl(url?: string): string {
  return url ?? window.location.pathname
}

function getReferrer(pageUrl: string): string {
  if (lastTrackedUrl && lastTrackedUrl !== pageUrl) return lastTrackedUrl
  return document.referrer || ''
}

type CollectPayloadFields = {
  website: string
  hostname: string
  language: string
  screen: string
  title: string
  url: string
  referrer: string
  name?: string
  id?: string
  data: Record<string, string | number | boolean>
}

function buildPayloadFields(
  name: string | undefined,
  data: Record<string, string | number | boolean>,
  distinctId?: string,
  url?: string,
): CollectPayloadFields | null {
  if (!analyticsRuntime) return null

  const pageUrl = getPageUrl(url)
  const userId = distinctId ?? analyticsRuntime.distinctId

  return {
    website: analyticsRuntime.websiteId,
    hostname: window.location.hostname,
    language: navigator.language,
    screen: `${window.screen.width}x${window.screen.height}`,
    title: document.title,
    url: pageUrl,
    referrer: getReferrer(pageUrl),
    ...(name ? { name } : {}),
    ...(userId ? { id: userId } : {}),
    data,
  }
}

function buildCollectPayload(
  type: 'event' | 'identify',
  name: string | undefined,
  data: Record<string, string | number | boolean>,
  distinctId?: string,
  url?: string,
) {
  const payload = buildPayloadFields(name, data, distinctId, url)
  if (!payload) return null

  return { type, payload }
}

async function postCollect(
  type: 'event' | 'identify',
  name: string | undefined,
  data: Record<string, string | number | boolean>,
  distinctId?: string,
  url?: string,
): Promise<void> {
  if (!analyticsRuntime) return

  // Re-resolve in case the tracker script loaded after initial configure (relative scriptUrl in DB).
  // Always derive from base path — never append /api/analytic-track to .../script.js
  const collectUrl = resolveCollectUrl(analyticsRuntime.baseScriptUrl) ?? analyticsRuntime.collectUrl
  if (!collectUrl.startsWith('http') && !collectUrl.startsWith('/')) return

  const absoluteCollectUrl = collectUrl.startsWith('/') ? `${window.location.origin}${collectUrl}` : collectUrl

  const body = buildCollectPayload(type, name, data, distinctId, url)
  if (!body) return

  try {
    await fetch(absoluteCollectUrl, {
      method: 'POST',
      keepalive: true,
      credentials: 'omit',
      headers: {
        'Content-Type': 'application/json',
        'x-umami-website-id': analyticsRuntime.websiteId,
        'x-umami-hostname': window.location.hostname,
      },
      body: JSON.stringify(body),
    })
  } catch {
    // swallow — analytics must never break CRM UX
  }
}

/**
 * Standard context stamped onto every event, however it was triggered (attribute, config or a
 * direct trackAnalyticsEvent call). Kept here so all three senders agree on the field names.
 */
export function buildAnalyticsEventContext(
  userId?: string | number,
  countryCode?: string,
): { userId?: string | number; country?: string; createTs: string } {
  return {
    userId,
    country: countryCode,
    createTs: new Date().toISOString(),
  }
}

function sendCollect(
  type: 'event' | 'identify',
  name: string | undefined,
  data: Record<string, string | number | boolean>,
  distinctId?: string,
  url?: string,
): void {
  const fields = buildPayloadFields(name, data, distinctId, url)
  if (!fields) return

  // Function form merges over the tracker payload (session token). Object-form `track({ url })`
  // replaces it and drops website/referrer; `identify(userId)` is how userId lands in `website`.
  if (type === 'event' && typeof window.umami?.track === 'function') {
    window.umami.track((props) => ({
      ...props,
      ...fields,
      website: fields.website,
      url: fields.url,
      referrer: fields.referrer,
    }))
    return
  }

  void postCollect(type, name, data, distinctId, url)
}

/**
 * Pageview. Triggered from app code because the tracker script runs with `data-auto-track="false"`
 * (see loadAnalyticsScript) — that switch is what stops it sending its own `data-umami-event`
 * clicks with a payload we cannot control.
 *
 * Path-only by default: filter changes rewrite the query via replaceState and must not split or
 * inflate Pages views.
 */
export function trackAnalyticsPageView(url?: string): void {
  if (typeof window === 'undefined') return

  const pageUrl = getPageUrl(url)
  sendCollect('event', undefined, {}, undefined, pageUrl)
  lastTrackedUrl = pageUrl
}

/**
 * Fire a custom event. Prefers `window.umami.track` when available; otherwise POSTs
 * directly to `/api/analytic-track` so tracking still works if the script fails to init.
 */
export function trackAnalyticsEvent(eventName: string, metadata: AnalyticsEventMetadata = {}): void {
  if (typeof window === 'undefined') return

  sendCollect('event', eventName, sanitizeMetadata(metadata))
}

/**
 * Tag the current session with a user id.
 *
 * Always POSTs our own identify body: `website` is the analytics website UUID, `id` / `data.userId`
 * carry the CRM user. Calling `umami.identify(userId)` is how some tracker builds put the user id
 * into `payload.website`.
 */
export function identifyAnalyticsUser(userId: string, data: AnalyticsEventMetadata = {}): void {
  if (typeof window === 'undefined') return

  if (analyticsRuntime) analyticsRuntime.distinctId = userId

  const sanitized = sanitizeMetadata({ ...data, userId })
  sendCollect('identify', undefined, sanitized, userId)
}

/**
 * Best-effort Umami script inject. The script is a transport only — it gives us window.umami.track
 * (and with it the session token); app code decides what gets sent. Custom events and pageviews do
 * not depend on this succeeding.
 * Reloads the script when websiteId changes (e.g. switching branch with a different branch_settings config).
 */
export function loadAnalyticsScript(scriptUrl: string, websiteId: string): Promise<boolean> {
  if (typeof window === 'undefined' || typeof document === 'undefined') return Promise.resolve(false)
  if (!scriptUrl || !websiteId) return Promise.resolve(false)

  configureAnalyticsRuntime(scriptUrl, websiteId)

  const existing = document.getElementById(ANALYTICS_SCRIPT_ID) as HTMLScriptElement | null
  const existingWebsiteId = existing?.getAttribute('data-website-id')

  if (existingWebsiteId === websiteId && typeof window.umami?.track === 'function') {
    return Promise.resolve(true)
  }

  if (existing) existing.remove()
  // Tracker only assigns window.umami once; clear so a new data-website-id takes effect.
  delete window.umami

  return new Promise((resolve) => {
    const script = document.createElement('script')
    script.id = ANALYTICS_SCRIPT_ID
    script.setAttribute('data-website-id', websiteId)
    // App code owns every send. Auto-track would otherwise be a second, uncontrollable sender:
    // it scrapes `data-umami-event` clicks itself and its payload drops the `data-umami-event-*`
    // extras on some tracker builds, and it would double count alongside our own listener.
    // Pageviews move to trackAnalyticsPageView (still path-only, replacing data-exclude-search).
    script.setAttribute('data-auto-track', 'false')
    // Set src last. Avoid defer/async so currentScript is available during Umami init when possible.
    script.src = resolveAnalyticsScriptUrl(scriptUrl)

    let settled = false
    const finish = (ok: boolean) => {
      if (settled) return
      settled = true
      script.onload = null
      script.onerror = null
      resolve(ok)
    }

    script.onload = () => {
      configureAnalyticsRuntime(script.src, websiteId)

      if (typeof window.umami?.track === 'function') {
        finish(true)
        return
      }
      // Poll briefly — some builds assign umami just after evaluate.
      let attempts = 0
      const timer = window.setInterval(() => {
        attempts += 1
        if (typeof window.umami?.track === 'function') {
          window.clearInterval(timer)
          finish(true)
        } else if (attempts >= 20) {
          window.clearInterval(timer)
          // Script loaded; collect API still usable via direct POST.
          finish(true)
        }
      }, 50)
    }
    script.onerror = () => finish(false)

    document.body.appendChild(script)
  })
}
